import { Type } from "@sinclair/typebox";
import { ApiErrorResponse } from "../ApiErrorResponse";
import { TypeBoxToType } from "../TypeBoxToType";
import { DbUser } from "@whatslly/server-commons/database/entities/users.entity";

export const DESTINATION_INTEGRATION_TYPES = {
  MESSAGES: "messages",
  AUDIT_LOGS: "auditLogs",
  DLP_INCIDENTS: "dlpIncidents",
  AUDIO_TRANSCRIBES: "audioTranscribes",
  DLP_DETECTORS: "dlpDetectors",
  SECURITY: "security",
  LLM_PROVIDER: "llmProvider",
  CHANNELS: "channels",
  PLATFORMS_CHANNELS: "platforms",
  ARCHIVE_CHANNELS: "archive",
} as const;

export const IncidentTypeValues = Type.Union([Type.Literal("dlp"), Type.Literal("surveillance"), Type.Literal("fraud")]);
export type IncidentTypeValue = TypeBoxToType<typeof IncidentTypeValues>;

export type DESTINATION_INTEGRATION_TYPES_TYPE = typeof DESTINATION_INTEGRATION_TYPES;

type OBJECT_IS_DESTINATION_TYPE<T extends DESTINATION_INTEGRATION_TYPES_TYPE[keyof DESTINATION_INTEGRATION_TYPES_TYPE]> = T extends "messages"
  ? MessagesIntegrations
  : T extends "audioTranscribes"
    ? TranscribeIntegrations
    : T extends "auditLogs"
      ? AuditLogsIntegrations
      : T extends "dlpIncidents"
        ? DlpIncidentsIntegrations
        : T extends "dlpDetectors"
          ? DlpDetectorsIntegrations
          : T extends "security"
            ? SecurityIntegrations
            : T extends "llmProvider"
              ? LlmProviderIntegrations
              : T extends "platforms"
                ? PlatformsChannelsIntegrations
                : T extends "channels"
                  ? ChannelsIntegrations
                  : T extends "archive"
                    ? ArchiveChannelsIntegrations
                    : undefined;

const isDestinationType = <T extends DESTINATION_INTEGRATION_TYPES_TYPE[keyof DESTINATION_INTEGRATION_TYPES_TYPE] = any>(
  obj: unknown,
  type: T
): obj is OBJECT_IS_DESTINATION_TYPE<T> => typeof obj === "object" && obj !== null && "integrationType" in obj && obj.integrationType === type;

export const isMessagesDestination = (obj: unknown): obj is MessagesIntegrations => isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.MESSAGES);
export const isBatchMessagesDestination = (obj: unknown): obj is BatchMessagesIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.MESSAGES) && ["proofpoint", "smtp"].includes(obj.type);
export const isAudioTranscribeDestination = (obj: unknown): obj is TranscribeIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES);
export const isAuditLogDestination = (obj: unknown): obj is AuditLogsIntegrations => isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS);
export const isDlpIncidentsDestination = (obj: unknown): obj is DlpIncidentsIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS);
export const isDlpDetectorsDestination = (obj: unknown): obj is DlpDetectorsIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS);
export const isSecurityDestination = (obj: unknown): obj is SecurityIntegrations => isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.SECURITY);
export const isLlmProviderDestination = (obj: unknown): obj is LlmProviderIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER);
export const isPlatformsChannelsDestination = (obj: unknown): obj is PlatformsChannelsIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS);
export const isChannelsDestination = (obj: unknown): obj is ChannelsIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.CHANNELS);
export const isArchiveChannelsDestination = (obj: unknown): obj is ArchiveChannelsIntegrations =>
  isDestinationType(obj, DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS);
export type MessagesIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.MESSAGES }>;
export type BatchMessagesIntegrations = Extract<
  MessageDestination,
  { type: "smtp" | "proofpoint"; integrationType: typeof DESTINATION_INTEGRATION_TYPES.MESSAGES }
>;
export type TranscribeIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES }>;
export type AuditLogsIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS }>;
export type DlpIncidentsIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS }>;
export type DlpDetectorsIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS }>;
export type SecurityIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.SECURITY }>;
export type LlmProviderIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER }>;
export type PlatformsChannelsIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS }>;
export type ChannelsIntegrations = Extract<MessageDestination, { integrationType: typeof DESTINATION_INTEGRATION_TYPES.CHANNELS }>;
export type ArchiveChannelsIntegrations = Extract<
  MessageDestination,
  { integrationType: typeof DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS }
>;
export type FeatureFlagRelatedIntegrations = Extract<
  MessageDestination,
  { isFeatureFlagRelated: true; integrationType: typeof DESTINATION_INTEGRATION_TYPES.MESSAGES }
>;

const DestinationBase = Type.Object({
  _id: Type.Optional(Type.String()),
  orgId: Type.String(),
  integrationName: Type.Optional(Type.String()),
  groups: Type.Optional(Type.Array(Type.String())),
  integrationType: Type.Union([
    Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS),
    Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS),
    Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES),
    Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS),
    Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
  ]),
  batchJob: Type.Optional(
    Type.Object({
      secret: Type.Optional(Type.String()),
      sync: Type.Optional(Type.Boolean()),
      cronExpression: Type.Optional(Type.String()),
    })
  ),
  scope: Type.Union([Type.Literal("org"), Type.Literal("selective")]),
  isViewOnly: Type.Optional(Type.Boolean()),
});

const CustomS3Base = Type.Object({
  bucket: Type.String(),
  mediaFilenameTemplate: Type.Optional(Type.String()),
  storeMedia: Type.Optional(Type.Boolean()),
  region: Type.Optional(Type.String()),
  accessKeyId: Type.Optional(Type.String()),
  secretAccessKey: Type.Optional(Type.String()),
  dek: Type.Optional(Type.String()),
});

const AmazonTranscribeBaseSettings = Type.Object({
  region: Type.String(),
  accessKeyId: Type.Optional(Type.String()),
  secretAccessKey: Type.Optional(Type.String()),
  option: Type.Union([
    Type.Object({ type: Type.Literal("specificLanguage"), languageCode: Type.String() }),
    Type.Object({
      type: Type.Literal("detectLanguage"),
      languageOptions: Type.Array(Type.String()),
      preferredLanguage: Type.Optional(Type.String()),
    }),
  ]),
});

const AmazonTranscribeSettings = Type.Union([
  Type.Intersect([
    AmazonTranscribeBaseSettings,
    Type.Object({
      allowLongAudioTranscription: Type.Optional(Type.Literal(false)),
    }),
  ]),
  Type.Intersect([
    AmazonTranscribeBaseSettings,
    Type.Object({
      allowLongAudioTranscription: Type.Literal(true),
      temporaryS3Bucket: Type.String(),
      temporaryS3Region: Type.String(),
      temporaryS3Prefix: Type.Optional(Type.String()),
    }),
  ]),
]);

const CRMServerToServerSettings = Type.Object({
  type: Type.Literal("server-to-server"),
  storeMedia: Type.Optional(Type.Boolean()),
  privateKey: Type.String(),
  certificate: Type.String(),
  username: Type.Optional(Type.String()),
  connectedAppConsumerKey: Type.String(),
  tenantId: Type.Optional(Type.String()),
  etoken: Type.String(),
  serverUrl: Type.String(),
});

const CRMServerToServerSettingsOnDataCloud = Type.Object({
  storeMedia: Type.Optional(Type.Boolean()),
  privateKey: Type.String(),
  certificate: Type.String(),
  username: Type.Optional(Type.String()),
  connectedAppConsumerKey: Type.String(),
  tenantId: Type.Optional(Type.String()),
  serverUrl: Type.String(),
  sf360tokens: Type.String(),
  sf360InstanceUrl: Type.String(),
  instanceUrl: Type.String(),
  connectorName: Type.String(),
  objectName: Type.String(),
});

const CRMWebAppSettings = Type.Object({
  type: Type.Literal("web-app"),
  storeMedia: Type.Optional(Type.Boolean()),
  etoken: Type.String(),
  orgId: Type.String(),
  userId: Type.String(),
  name: Type.String(),
});

const CRMCreateObjectSettings = Type.Object({
  fields: Type.Record(Type.String(), Type.String()),
  type: Type.Union([Type.Literal("contact"), Type.Literal("lead")]),
});

const MicrosoftPurviewSettings = Type.Object({
  privateKey: Type.String(),
  certificate: Type.String(),
  connectedAppConsumerKey: Type.String(),
  tenantId: Type.String(),
  accessToken: Type.String(),
  userId: Type.String(),
});

export type MicrosoftPurviewSettings = TypeBoxToType<typeof MicrosoftPurviewSettings>;

const webhookSettings = Type.Object({
  tls: Type.Boolean(),
  host: Type.String(),
  port: Type.String(),
});

const azureStorageSharedKeySettings = Type.Object({
  type: Type.Literal("storage-shared-key-credential"),
  format: Type.Union([Type.Literal("json"), Type.Literal("csv")]),
  accountName: Type.String(),
  accountKey: Type.String(),
  containerName: Type.String(),
  filenameTemplate: Type.String(),
  mediaFilenameTemplate: Type.Optional(Type.String()),
  storeMedia: Type.Optional(Type.Boolean()),
  dek: Type.Optional(Type.String()),
});

const azureClientSecretSettings = Type.Object({
  type: Type.Literal("client-secret-credential"),
  format: Type.Union([Type.Literal("json"), Type.Literal("csv")]),
  containerName: Type.String(),
  filenameTemplate: Type.String(),
  mediaFilenameTemplate: Type.Optional(Type.String()),
  storeMedia: Type.Optional(Type.Boolean()),
  tenantId: Type.String(),
  clientId: Type.String(),
  clientSecret: Type.String(),
  url: Type.String(),
  dek: Type.Optional(Type.String()),
});

export type azureStorageSharedKeySettings = TypeBoxToType<typeof azureStorageSharedKeySettings>;
export type azureClientSecretSettings = TypeBoxToType<typeof azureClientSecretSettings>;

const Destination = Type.Union([
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS),
    type: Type.Literal("forcepointDlpDetector"),
    settings: Type.Object({
      serverUrl: Type.String(),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS),
    type: Type.Literal("googleSecOpsAuditLog"),
    settings: Type.Object({
      webhookAccessKey: Type.String(),
      serverUrl: Type.String(),
      googApiKey: Type.String(),
      namespaces: Type.Array(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS),
    type: Type.Literal("googleSecOpsDlpIncident"),
    settings: Type.Object({
      webhookAccessKey: Type.String(),
      serverUrl: Type.String(),
      googApiKey: Type.String(),
      incidentTypes: Type.Optional(Type.Array(IncidentTypeValues)),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS),
    type: Type.Literal("splunkAuditLog"),
    settings: Type.Object({
      apiKey: Type.String(),
      serverUrl: Type.String(),
      namespaces: Type.Array(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS),
    type: Type.Literal("splunkDlpIncident"),
    settings: Type.Object({
      apiKey: Type.String(),
      serverUrl: Type.String(),
      incidentTypes: Type.Optional(Type.Array(IncidentTypeValues)),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS),
    type: Type.Literal("elasticAuditLog"),
    settings: Type.Object({
      apiKey: Type.String(),
      index: Type.String(),
      serverUrl: Type.String(),
      pipeline: Type.Optional(Type.String()),
      namespaces: Type.Array(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS),
    type: Type.Literal("elasticDlpIncident"),
    settings: Type.Object({
      apiKey: Type.String(),
      index: Type.String(),
      serverUrl: Type.String(),
      pipeline: Type.Optional(Type.String()),
      incidentTypes: Type.Optional(Type.Array(IncidentTypeValues)),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS),
    type: Type.Literal("qradarDlpIncident"),
    settings: Type.Intersect([
      webhookSettings,
      Type.Object({
        incidentTypes: Type.Optional(Type.Array(IncidentTypeValues)),
      }),
    ]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS),
    type: Type.Literal("sentinelDlpIncident"),
    settings: Type.Object({
      tenantId: Type.String(),
      clientId: Type.String(),
      clientSecret: Type.String(),
      dceEndpoint: Type.String(),
      dcrImmutableId: Type.String(),
      streamName: Type.Optional(Type.String()),
      incidentTypes: Type.Optional(Type.Array(IncidentTypeValues)),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS),
    type: Type.Literal("qradarAuditLog"),
    settings: Type.Intersect([
      webhookSettings,
      Type.Object({
        namespaces: Type.Array(Type.String()),
      }),
    ]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES),
    type: Type.Literal("azureSpeechTranscribe"),
    settings: Type.Object({
      region: Type.String(),
      subscriptionKey: Type.String(),
      option: Type.Union([Type.Object({ type: Type.Literal("specificLanguage"), languageCode: Type.String() })]),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES),
    type: Type.Literal("amazonTranscribe"),
    settings: AmazonTranscribeSettings,
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("s3"),
    settings: Type.Optional(Type.Object({})),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("batch"),
    settings: Type.Optional(Type.Object({})),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("coralogix"),
    settings: Type.Object({
      apiKey: Type.String(),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("splunk"),
    settings: Type.Object({
      apiKey: Type.String(),
      serverUrl: Type.String(),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("crowdstrike"),
    settings: Type.Object({
      apiKey: Type.String(),
      apiUrl: Type.String(),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("sftp"),
    settings: Type.Object({
      format: Type.Union([Type.Literal("json"), Type.Literal("csv")]),
      host: Type.String(),
      port: Type.Optional(Type.String()),
      shouldIncludeUploadTime: Type.Optional(Type.Boolean()),
      fileNameTimeZone: Type.Optional(Type.String()),
      noFolderStructureEnabled: Type.Optional(Type.Boolean()),
      saveFileZipped: Type.Optional(Type.Boolean()),
      filenameTemplate: Type.String(),
      mediaFilenameTemplate: Type.Optional(Type.String()),
      storeMedia: Type.Optional(Type.Boolean()),
      username: Type.String(),
      basePath: Type.Optional(Type.String()),
      readBasePath: Type.Optional(Type.String()),
      password: Type.Optional(Type.String()),
      privateKey: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("customS3"),
    settings: Type.Intersect([
      CustomS3Base,
      Type.Object({
        format: Type.Union([Type.Literal("json"), Type.Literal("csv"), Type.Literal("parquet")]),
        filenameTemplate: Type.String(),
        externalId: Type.Optional(Type.String()),
        roleArn: Type.Optional(Type.String()),
      }),
    ]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("r2"),
    settings: Type.Intersect([
      Type.Object({
        bucket: Type.String(),
        accountId: Type.String(),
        accessKeyId: Type.Optional(Type.String()),
        secretAccessKey: Type.Optional(Type.String()),
        mediaFilenameTemplate: Type.Optional(Type.String()),
        storeMedia: Type.Optional(Type.Boolean()),
        dek: Type.Optional(Type.String()),
      }),
      Type.Object({
        format: Type.Union([Type.Literal("json"), Type.Literal("csv")]),
        filenameTemplate: Type.String(),
      }),
    ]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("redshift"),
    settings: Type.Intersect([
      CustomS3Base,
      Type.Object({
        connectionString: Type.String(),
      }),
    ]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("googleCloudStorage"),
    settings: Type.Object({
      format: Type.Union([Type.Literal("json"), Type.Literal("csv")]),
      bucket: Type.String(),
      filenameTemplate: Type.String(),
      mediaFilenameTemplate: Type.Optional(Type.String()),
      storeMedia: Type.Optional(Type.Boolean()),
      projectId: Type.Optional(Type.String()),
      client_email: Type.Optional(Type.String()),
      private_key: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("smtp"),
    settings: Type.Object({
      host: Type.String(),
      port: Type.Number(),
      secure: Type.Optional(Type.Boolean()),
      headers: Type.Optional(Type.Object({})),
      user: Type.String(),
      password: Type.String(),
      rcpt: Type.String(),
      storeMedia: Type.Optional(Type.Boolean()),
      msgDateTimeZone: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("proofpoint"),
    settings: Type.Object({
      rcpt: Type.String(),
      storeMedia: Type.Optional(Type.Boolean()),
      msgDateTimeZone: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("azureBlob"),
    settings: Type.Union([azureStorageSharedKeySettings, azureClientSecretSettings]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("salesforce"),
    settings: Type.Union([CRMWebAppSettings, CRMServerToServerSettings]),
    extraSettings: Type.Optional(Type.Object({ createObjectSettings: Type.Optional(CRMCreateObjectSettings) })),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("salesforceDataCloud"),
    settings: Type.Union([CRMServerToServerSettingsOnDataCloud]),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("dynamics"),
    settings: Type.Union([CRMWebAppSettings, CRMServerToServerSettings]),
    extraSettings: Type.Optional(Type.Object({ createObjectSettings: Type.Optional(CRMCreateObjectSettings) })),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS),
    type: Type.Literal("microsoftPurviewDlpDetector"),
    settings: MicrosoftPurviewSettings,
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("analytics"),
    settings: Type.Optional(Type.Object({})),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("urlAnalytics"),
    settings: Type.Optional(Type.Object({})),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("verint"),
    settings: Type.Object({
      serverUrl: Type.String(),
      apiKeyId: Type.String(),
      apiKey: Type.String(),
      storeId: Type.String(),
      projectId: Type.String(),
      datasource: Type.String(),
      storeMedia: Type.Optional(Type.Boolean()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("qradar"),
    settings: webhookSettings,
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("internalS3"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("internalMongoDb"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("paloAltoFirewall"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("checkpointFirewall"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("sentinelOneFirewall"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("skyhighProxy"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("netskopeCasbProxy"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("crowdStrikeEndpointProtection"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("broadcomProxySg"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("forcepointProxyWebSecurity"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("fortinetFortigateProxy"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("trellixProxy"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("trendMicroEeEndpointProtection"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.SECURITY),
    type: Type.Literal("kasperskyEndpoint"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("tuvisDLP"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
    isFeatureFlagRelated: Type.Literal(true),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("tuvisKeywordInsights"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
    isFeatureFlagRelated: Type.Literal(true),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("tuvisSurveillance"),
    settings: Type.Object({}),
    extraSettings: Type.Optional(Type.Object({})),
    isFeatureFlagRelated: Type.Literal(true),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("hubspot"),
    settings: Type.Object({
      storeMedia: Type.Optional(Type.Boolean()),
      tokens: Type.String(),
      hubDomain: Type.String(),
      hubId: Type.Number(),
      userId: Type.Number(),
      user: Type.String(),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.MESSAGES),
    type: Type.Literal("pipedrive"),
    settings: Type.Object({
      tokens: Type.String(),
      user: Type.String(),
      userId: Type.String(),
      companyDomain: Type.String(),
      companyId: Type.String(),
      storeMedia: Type.Boolean(),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
    type: Type.Literal("openai"),
    settings: Type.Object({
      apiKey: Type.String(),
      enabledModels: Type.Optional(Type.Array(Type.String())),
      defaultModel: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
    type: Type.Literal("anthropic"),
    settings: Type.Object({
      apiKey: Type.String(),
      enabledModels: Type.Optional(Type.Array(Type.String())),
      defaultModel: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
    type: Type.Literal("google"),
    settings: Type.Object({
      apiKey: Type.String(),
      enabledModels: Type.Optional(Type.Array(Type.String())),
      defaultModel: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
    type: Type.Literal("azure_openai"),
    settings: Type.Object({
      apiKey: Type.String(),
      endpoint: Type.String(),
      deploymentName: Type.String(),
      apiVersion: Type.Optional(Type.String()),
      enabledModels: Type.Optional(Type.Array(Type.String())),
      defaultModel: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
    type: Type.Literal("bedrock"),
    settings: Type.Object({
      region: Type.String(),
      accessKeyId: Type.String(),
      secretAccessKey: Type.String(),
      sessionToken: Type.Optional(Type.String()),
      enabledModels: Type.Optional(Type.Array(Type.String())),
      defaultModel: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
  Type.Object({
    integrationType: Type.Literal(DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER),
    type: Type.Literal("internalAzureOpenAi"),
    settings: Type.Object({
      enabledModels: Type.Optional(Type.Array(Type.String())),
      defaultModel: Type.Optional(Type.String()),
    }),
    extraSettings: Type.Optional(Type.Object({})),
  }),
]);

export const MASK_SETTINGS_FIELDS: Record<Destination["type"], string[]> = {
  amazonTranscribe: ["secretAccessKey"],
  azureBlob: ["clientSecret", "accountKey"],
  azureSpeechTranscribe: ["subscriptionKey"],
  coralogix: ["apiKey"],
  customS3: ["secretAccessKey"],
  r2: ["secretAccessKey"],
  smtp: ["password"],
  redshift: ["secretAccessKey", "connectionString"],
  sftp: ["password"],
  salesforce: ["connectedAppConsumerKey"],
  salesforceDataCloud: ["connectedAppConsumerKey"],
  crowdstrike: ["apiKey"],
  googleSecOpsAuditLog: ["googApiKey", "webhookAccessKey"],
  googleSecOpsDlpIncident: ["googApiKey", "webhookAccessKey"],
  splunk: ["apiKey"],
  splunkAuditLog: ["apiKey"],
  splunkDlpIncident: ["apiKey"],
  elasticAuditLog: ["apiKey"],
  elasticDlpIncident: ["apiKey"],
  verint: ["apiKey"],
  s3: [],
  googleCloudStorage: ["private_key"],
  analytics: [],
  urlAnalytics: [],
  batch: [],
  qradar: [],
  qradarDlpIncident: [],
  sentinelDlpIncident: ["clientSecret"],
  qradarAuditLog: [],
  dynamics: [],
  internalMongoDb: [],
  internalS3: [],
  proofpoint: [],
  tuvisDLP: [],
  tuvisKeywordInsights: [],
  tuvisSurveillance: [],
  hubspot: ["tokens"],
  pipedrive: ["tokens"],
  forcepointDlpDetector: [],
  microsoftPurviewDlpDetector: [],
  paloAltoFirewall: [],
  checkpointFirewall: [],
  sentinelOneFirewall: [],
  skyhighProxy: [],
  netskopeCasbProxy: [],
  crowdStrikeEndpointProtection: [],
  broadcomProxySg: [],
  forcepointProxyWebSecurity: [],
  fortinetFortigateProxy: [],
  trellixProxy: [],
  trendMicroEeEndpointProtection: [],
  kasperskyEndpoint: [],
  openai: ["apiKey"],
  anthropic: ["apiKey"],
  google: ["apiKey"],
  azure_openai: ["apiKey"],
  bedrock: ["accessKeyId", "secretAccessKey", "sessionToken"],
  internalAzureOpenAi: [],
};

export type Destination = TypeBoxToType<typeof Destination>;

export type CRMServerToServerSettings = TypeBoxToType<typeof CRMServerToServerSettings>;
export type CRMServerToServerSettingsOnDataCloud = TypeBoxToType<typeof CRMServerToServerSettingsOnDataCloud>;
export type CRMWebAppSettings = TypeBoxToType<typeof CRMWebAppSettings>;
export type CRMCreateObjectSettings = TypeBoxToType<typeof CRMCreateObjectSettings>;

export const MessageDestination = Type.Intersect([DestinationBase, Destination]);
export type MessageDestination = TypeBoxToType<typeof MessageDestination>;

export const OrgIdUrlParams = Type.Object({ orgId: Type.String() });
export type OrgIdUrlParams = TypeBoxToType<typeof OrgIdUrlParams>;

export const DestinationUrlParams = Type.Intersect([OrgIdUrlParams, Type.Object({ destinationId: Type.String() })]);
export type DestinationUrlParams = TypeBoxToType<typeof DestinationUrlParams>;

export const ListDestinationsDestinationResponse = Type.Intersect([
  MessageDestination,
  Type.Object({
    readable: Type.Optional(Type.Boolean()),
    enableDatesFilter: Type.Optional(Type.Boolean()),
    canReadAll: Type.Optional(Type.Boolean()),
  }),
]);

export type ListDestinationsDestinationResponse = TypeBoxToType<typeof ListDestinationsDestinationResponse>;

export const ListDestinationsResponse = Type.Union([
  Type.Object({
    success: Type.Literal(true),
    destinations: Type.Array(ListDestinationsDestinationResponse),
  }),
  ApiErrorResponse,
]);
export type ListDestinationsResponse = TypeBoxToType<typeof ListDestinationsResponse>;

export const ListDestinationsDestinationResponseShallow = Type.Omit(ListDestinationsDestinationResponse, ["settings"]);
export type ListDestinationsDestinationResponseShallow = TypeBoxToType<typeof ListDestinationsDestinationResponseShallow>;

export const ListDestinationsShallowResponse = Type.Union([
  Type.Object({
    success: Type.Literal(true),
    destinations: Type.Array(ListDestinationsDestinationResponseShallow),
  }),
  ApiErrorResponse,
]);
export type ListDestinationsShallowResponse = TypeBoxToType<typeof ListDestinationsShallowResponse>;

export const HasDestinationsResponse = Type.Object({ hasDestinations: Type.Boolean() });
export type HasDestinationsResponse = TypeBoxToType<typeof HasDestinationsResponse>;

export const GenerateCertificateResponseSuccess = Type.Object({
  success: Type.Literal(true),
  privateKey: Type.String(),
  publicKey: Type.String(),
  certificate: Type.String(),
});

export const GenerateCertificateResponse = Type.Union([GenerateCertificateResponseSuccess, ApiErrorResponse]);

export type GenerateCertificateResponse = TypeBoxToType<typeof GenerateCertificateResponse>;

export type GenerateCertificateResponseSuccess = TypeBoxToType<typeof GenerateCertificateResponseSuccess>;

export const TestConnectionResponseSuccess = Type.Object({
  success: Type.Literal(true),
});

export const TestConnectionResponse = Type.Union([TestConnectionResponseSuccess, ApiErrorResponse]);
export type TestConnectionResponse = TypeBoxToType<typeof TestConnectionResponse>;

export const UpdateDestinationDetailsBody = Type.Object({
  integrationName: Type.Optional(Type.String()),
  groups: Type.Optional(Type.Array(Type.String())),
  isViewOnly: Type.Optional(Type.Boolean()),
});
export type UpdateDestinationDetailsBody = TypeBoxToType<typeof UpdateDestinationDetailsBody>;

export const isAssignToGroup = (user: DbUser | undefined | null, integration: MessageDestination) => {
  if (!Array.isArray(integration.groups) || integration.groups.length === 0) {
    return true;
  }
  const userGroupIds = user?.groupIds;
  if (!Array.isArray(userGroupIds) || userGroupIds.length === 0) {
    return false;
  }
  const userGroupIdsStr = userGroupIds.map((g) => g.toString());
  return integration.groups.some((groupsId) => userGroupIdsStr.includes(groupsId));
};

export const hasIntegrationWithGroups = (integrations: MessageDestination[]) => {
  return integrations.some((integration) => Array.isArray(integration.groups) && integration.groups.length > 0);
};
